import { HTTPStatusError } from './chat-api';

export type WebsiteBBConnectorStatus = 'pending' | 'active' | 'revoked';

export type WebsiteBBConnector = {
  id: string;
  device_id?: string | null;
  name: string;
  alias: string;
  root: string;
  description?: string;
  platform?: string;
  arch?: string;
  version?: string;
  status: WebsiteBBConnectorStatus;
  online?: boolean;
  environment_status?: string;
  last_env_id?: string;
  last_seen_at?: string | null;
  bootstrapped_at?: string | null;
  revoked_at?: string | null;
  created_at: string;
  updated_at: string;
};

export type WebsiteBBCreateConnectorInput = {
  name?: string;
  alias?: string;
  root?: string;
  description?: string;
};

export type WebsiteBBCreateConnectorResult = {
  connector: WebsiteBBConnector;
  bootstrap_token: string;
  connect_url: string;
  suggested_command: string;
};

function buildErrorMessage(status: number, body: unknown): string {
  if (
    typeof body === 'object' &&
    body !== null &&
    'error' in body &&
    typeof body.error === 'object' &&
    body.error !== null &&
    'message' in body.error &&
    typeof body.error.message === 'string'
  ) {
    return body.error.message;
  }

  if (typeof body === 'string' && body.trim()) {
    return body.trim();
  }

  return `Request failed (${status})`;
}

async function readErrorBody(response: Response): Promise<unknown> {
  const contentType = response.headers.get('content-type') ?? '';
  if (contentType.includes('application/json')) {
    return response.json().catch(() => null);
  }

  return response.text().catch(() => null);
}

async function fetchBBJSON<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    credentials: 'include',
    ...init,
  });

  if (!response.ok) {
    const body = await readErrorBody(response);
    throw new HTTPStatusError(
      response.status,
      buildErrorMessage(response.status, body)
    );
  }

  return (await response.json()) as T;
}

export async function listBBConnectors(): Promise<WebsiteBBConnector[]> {
  const response = await fetchBBJSON<{ connectors: WebsiteBBConnector[] }>(
    '/v1/user/bb/connectors'
  );
  return response.connectors ?? [];
}

export async function createBBConnector(
  input: WebsiteBBCreateConnectorInput
): Promise<WebsiteBBCreateConnectorResult> {
  return fetchBBJSON<WebsiteBBCreateConnectorResult>('/v1/user/bb/connectors', {
    method: 'POST',
    headers: {
      accept: 'application/json',
      'content-type': 'application/json',
    },
    body: JSON.stringify(input),
  });
}

export async function revokeBBConnector(
  connectorID: string
): Promise<WebsiteBBConnector> {
  const response = await fetchBBJSON<{ connector: WebsiteBBConnector }>(
    `/v1/user/bb/connectors/${encodeURIComponent(connectorID)}/revoke`,
    {
      method: 'POST',
      headers: {
        accept: 'application/json',
      },
    }
  );
  return response.connector;
}

export async function updateBBConnector(
  connectorID: string,
  input: WebsiteBBCreateConnectorInput
): Promise<WebsiteBBConnector> {
  const response = await fetchBBJSON<{ connector: WebsiteBBConnector }>(
    `/v1/user/bb/connectors/${encodeURIComponent(connectorID)}`,
    {
      method: 'PATCH',
      headers: {
        accept: 'application/json',
        'content-type': 'application/json',
      },
      body: JSON.stringify(input),
    }
  );
  return response.connector;
}
